import { z } from 'zod'

/**
 * Money is an integer number of paisa, in a key suffixed `Paisa`. Never a
 * float (0.1 + 0.2 is not 0.3 and a trader's ledger may not disagree with his
 * own arithmetic), never a preformatted string (formatting is a display
 * concern and Urdu is coming).
 *
 * `paisaSchema` is signed: a profit can be a loss, an unrealised position can
 * be negative. Amounts a client *sends* use the positive/non-negative forms.
 */
export const paisaSchema = z.number().int('Money is whole paisa.')
export const positivePaisaSchema = z.number().int().positive()
export const nonNegativePaisaSchema = z.number().int().nonnegative()

/**
 * A money field in a **response**.
 *
 * Optional — not nullable — because §4.16 says the key is *absent from the
 * JSON* for a Staff account or a price-locked session, rather than null,
 * zeroed or masked. Typing it optional is what forces both frontends to write
 * the `••••••` branch at compile time instead of remembering to.
 *
 * Use this for every derived or stored figure in a response. Requests use
 * `positivePaisaSchema` and friends, because a client sending money is always
 * sending a real number.
 */
export const moneyPaisa = () => paisaSchema.optional()

/** The same, for a figure that is meaningfully absent *and* meaningfully null. */
export const nullableMoneyPaisa = () => paisaSchema.nullable().optional()

/**
 * Basis points. Commission is stored and transmitted as bps (200 = 2%) so the
 * rate is an integer too; break-even is solved for with it, never added.
 */
export const bpsSchema = z.number().int().min(0).max(10_000)

/** A ratio or percentage a server computed (ROI, share of spend). Not money. */
export const ratioSchema = z.number()

/** Bytes — storage quotas and media sizes. Not money, never masked. */
export const bytesSchema = z.number().int().nonnegative()

/** A limit that may be unlimited. `null` means unlimited, per plan §4.17. */
export const limitSchema = z.number().int().nonnegative().nullable()
